import { escapeHtml } from '../util/escapeHtml.js';
import { anchored } from '../util/viewportAnchor.js';
import { mountShell } from '../components/Shell.js';
import { api, HttpError } from '../api/client.js';
import { store } from '../state/store.js';
import { formatCrowns } from '../util/formatBig.js';
import { cgTable, cgCol, cgRow } from '../util/costReward.js';
import type { BankDto, BankTierDto } from '@rex-mortis/shared';

interface FlashState {
  kind: 'success' | 'danger' | 'warn';
  message: string;
}

/**
 * Live-interest ticker handle. Bank interest is continuous (per-second), so the
 * summary panel shows it accruing in real time. Only one timer runs at a time —
 * it's cleared on every re-render and self-clears once its element is detached.
 */
let interestTicker: number | null = null;
function clearInterestTicker(): void {
  if (interestTicker !== null) {
    window.clearInterval(interestTicker);
    interestTicker = null;
  }
}

export async function renderBanks(root: HTMLElement): Promise<void> {
  let bank: BankDto | null = null;
  let tiers: BankTierDto[] = [];
  let flash: FlashState | null = null;

  async function refresh(): Promise<void> {
    try {
      const r = await api.getBank();
      store.set({ character: r.character });
      bank = r.bank;
      tiers = r.tiers;
    } catch (err) {
      const msg = err instanceof HttpError ? err.message : 'Failed to load the Counting House.';
      flash = { kind: 'danger', message: msg };
    }
  }

  await refresh();

  mountShell(root, (main) => {
    const renderMain = (): void => {
      clearInterestTicker();
      const char = store.get().character;
      if (!char || !bank) {
        main.innerHTML = '<p>Loading…</p>';
        return;
      }

      // The Counting House keeps its doors only in Rex Mortis — while travelling
      // or abroad you can neither deposit nor withdraw, so coin must ride in your
      // purse (and brave the roads). Mirrors the server-side guard in bankService.
      const awayFromHome = char.status === 'TRAVELLING' || char.location !== 'rex_mortis';
      if (awayFromHome) {
        main.innerHTML = `
          <img class="page-banner" src="/img/banners/bank.webp" alt="" />
          <h1>The Counting House</h1>
          <div class="panel" style="text-align:center; padding: var(--s-5);">
            <p style="font-family: var(--font-display); font-size: 1.3rem; color: var(--c-oxblood); margin: 0 0 10px;">The doors are barred.</p>
            <p style="font-style:italic; color: var(--c-ink-soft); margin: 0; line-height: 1.5;">
              The Counting House keeps its doors only in Rex Mortis. While you are on the road or abroad,
              your coin must ride in your purse — and brave the roads. Return home to deposit or withdraw.
            </p>
          </div>`;
        return;
      }

      main.innerHTML = `
        <img class="page-banner" src="/img/banners/bank.webp" alt="" />
        <h1>The Counting House</h1>
        <p style="font-style:italic; color: var(--c-ink-soft); margin-top: -8px;">
          Coins under another roof. Coins that quietly multiply.
        </p>

        <div id="flash"></div>

        <div class="panel bank-summary">
          <div class="bank-summary-head">
            <div>
              <h2 style="margin: 0;">${escapeHtml(bank.tierName)}</h2>
              <p style="font-style:italic; color: var(--c-ink-soft); margin: 2px 0 0 0;">"${escapeHtml(bank.tierBlurb)}"</p>
            </div>
            <span class="pill gold" style="font-size: 1.1rem; padding: 6px 14px;">${bank.ratePerDayPct}% / day</span>
          </div>
          <div class="bank-figures">
            <div class="bank-figure">
              <div class="label">On deposit</div>
              <div class="value gold">${fmtBig(bank.principalCrowns)}</div>
              <div class="cap">cap: ${fmtBig(bank.capCrowns)}</div>
            </div>
            <div class="bank-figure">
              <div class="label">Crowns on hand</div>
              <div class="value">${fmtBig(char.crowns)}</div>
              <div class="cap">your wallet — vulnerable to plunder</div>
            </div>
            <div class="bank-figure">
              <div class="label">Daily interest</div>
              <div class="value">+${fmtBig(bank.interestPerDayCrowns)}</div>
              <div class="cap">${bank.capacityFull ? 'overflows to your wallet (vault full)' : 'compounds into your vault each day'}</div>
            </div>
            <div class="bank-figure">
              <div class="label">Next interest</div>
              <div class="value gold" id="next-interest">—</div>
              <div class="cap" id="next-interest-note">${bank.capacityFull ? 'one lump, straight to your wallet' : 'one lump, into your vault'}</div>
            </div>
          </div>
          <div class="bank-progress" title="Deposit cap usage">
            <span class="bank-progress-fill" style="width: ${capPct(bank).toFixed(2)}%;"></span>
          </div>
          ${bank.capacityFull ? '<p class="alert warn" style="margin-top: var(--s-3);">Your vault is at capacity. Upgrade to deposit more, or withdraw some first.</p>' : ''}
        </div>

        <div class="panel">
          <h2 style="margin-top: 0;">Move coin</h2>
          <div class="bank-controls">
            <div class="bank-control">
              <label for="dep-amt">Deposit</label>
              <div class="bank-control-row">
                <input type="text" inputmode="numeric" pattern="[0-9]*" id="dep-amt" placeholder="0" />
                <button class="btn ghost" data-fill="dep">Max</button>
                <button class="btn" id="dep-btn">Deposit</button>
              </div>
            </div>
            <div class="bank-control">
              <label for="wd-amt">Withdraw</label>
              <div class="bank-control-row">
                <input type="text" inputmode="numeric" pattern="[0-9]*" id="wd-amt" placeholder="0" />
                <button class="btn ghost" data-fill="wd">Max</button>
                <button class="btn" id="wd-btn">Withdraw</button>
              </div>
            </div>
          </div>
        </div>

        <div class="panel">
          <h2 style="margin-top: 0;">Bank tiers</h2>
          <p style="font-style: italic; color: var(--c-ink-soft); margin-top: 0;">
            Each upgrade is paid once. Higher tiers carry larger sums and pay better interest.
          </p>
          <div class="tier-list" id="tier-list"></div>
        </div>
      `;

      // ── Inline page styles ──────────────────────────────────────────────
      const style = document.createElement('style');
      style.textContent = `
        .bank-summary-head {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: var(--s-4);
          flex-wrap: wrap;
        }
        .bank-figures {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: var(--s-4);
          margin-top: var(--s-4);
        }
        .bank-figure {
          background: rgba(255, 245, 220, 0.55);
          border: 1px solid var(--c-rule);
          border-radius: var(--radius);
          padding: var(--s-3);
        }
        .bank-figure .label {
          font-family: var(--font-display);
          font-size: 0.9rem;
          color: var(--c-ink-soft);
          letter-spacing: 0.03em;
        }
        .bank-figure .value {
          font-family: var(--font-numeric);
          font-weight: 700;
          font-size: 1.5rem;
          color: var(--c-ink);
          margin: 4px 0;
        }
        .bank-figure .value.gold { color: #6e5510; }
        .bank-figure .cap {
          font-family: var(--font-elegant);
          font-style: italic;
          font-size: 0.92rem;
          color: var(--c-ink-soft);
        }
        .bank-progress {
          margin-top: var(--s-3);
          height: 8px;
          background: rgba(28, 17, 8, 0.18);
          border: 1px solid var(--c-rule-strong);
          border-radius: 2px;
          overflow: hidden;
          box-shadow: var(--shadow-press);
        }
        .bank-progress-fill {
          display: block;
          height: 100%;
          background: linear-gradient(to bottom, var(--c-gold-bright), var(--c-gold));
          transition: width 0.3s ease;
        }
        .bank-controls {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: var(--s-4);
        }
        .bank-control {
          min-width: 0;        /* allow this column to shrink inside the grid */
          display: flex;
          flex-direction: column;
        }
        .bank-control label {
          display: block;
          font-family: var(--font-display);
          font-size: 0.95rem;
          color: var(--c-ink-soft);
          margin-bottom: 4px;
          letter-spacing: 0.03em;
        }
        .bank-control-row {
          display: flex;
          flex-wrap: wrap;     /* stack onto a second line if too narrow */
          gap: var(--s-2);
          align-items: stretch;
          min-width: 0;
        }
        .bank-control-row input {
          flex: 1 1 140px;
          min-width: 0;        /* allow the input to shrink past its content default */
          width: 100%;
          font: inherit;
          font-family: var(--font-numeric);
          padding: var(--s-2) var(--s-3);
          /* Colours come from the global pinned cream cell in styles/pages.css. */
          border: 1px solid var(--c-rule);
          border-radius: var(--radius);
          box-shadow: var(--shadow-press);
        }
        .bank-control-row input:focus {
          outline: none;
          border-color: var(--c-gold);
          box-shadow: var(--shadow-press), 0 0 0 3px rgba(212, 169, 60, 0.25);
        }
        .bank-control-row .btn {
          white-space: nowrap;
          flex: 0 0 auto;
        }
        .tier-list {
          display: flex;
          flex-direction: column;
          gap: var(--s-3);
        }
        .tier-row {
          display: flex;
          gap: var(--s-3);
          align-items: center;
          flex-wrap: wrap;
          padding: var(--s-3);
          background: var(--c-parchment);
          background-image: var(--parchment-texture);
          border: 1px solid var(--c-rule-strong);
          border-radius: var(--radius);
          box-shadow: var(--shadow-soft);
        }
        @media (max-width: 480px) {
          .tier-row .tier-cta { flex: 1 1 100%; }
          .tier-row .tier-cta .btn { width: 100%; }
        }
        .tier-row.active {
          border-color: var(--c-oxblood);
          box-shadow: var(--shadow-soft), inset 0 0 0 2px rgba(107, 31, 31, 0.18);
        }
        .tier-row.locked { opacity: 0.55; }
        .tier-medal {
          flex: 0 0 auto;
          position: relative;
          width: 96px;
          height: 96px;
          border-radius: 50%;
          background: linear-gradient(135deg, var(--c-gold-pale), var(--c-gold));
          border: 1px solid #5a3f08;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: var(--font-numeric);
          font-weight: 800;
          font-size: 1.6rem;
          color: #2a1f08;
          box-shadow:
            inset 0 1px 0 rgba(255, 245, 220, 0.45),
            inset 0 -1px 0 rgba(28, 17, 8, 0.30),
            0 1px 2px rgba(28, 17, 8, 0.20);
        }
        /* Per-tier coin artwork — a distinct minted coin for each interest tier.
           Sits over the numbered fallback medal; onerror removes it so the
           number shows through until the asset exists in /img/bank-coins/. */
        .tier-medal .tier-coin {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          border-radius: 50%;
        }
        /* flex-basis 0 (not auto) — otherwise a long blurb's intrinsic width
           pushes medal+body+cta past the row and flex-wrap breaks the layout
           onto separate lines. With basis 0 the body just takes the remaining
           space and wraps its own text. */
        .tier-body { flex: 1 1 0; min-width: 0; }
        .tier-body .name {
          font-family: var(--font-display);
          font-size: 1.05rem;
          font-weight: 700;
          color: var(--c-ink);
          letter-spacing: 0.03em;
        }
        .tier-body .blurb {
          font-family: var(--font-elegant);
          font-style: italic;
          font-size: 0.92rem;
          color: var(--c-ink-soft);
          margin: 2px 0 4px 0;
        }
        .tier-stats {
          display: flex;
          gap: var(--s-3);
          flex-wrap: wrap;
          font-size: 0.88rem;
          color: var(--c-ink-soft);
        }
        .tier-stats strong { color: var(--c-ink); }
        .tier-cta {
          flex: 0 0 auto;
        }
        .tier-cta .status {
          font-family: var(--font-display);
          font-size: 0.92rem;
          color: var(--c-ink-soft);
          letter-spacing: 0.04em;
        }
        .tier-cta .status.owned { color: var(--c-success); }
      `;
      main.appendChild(style);

      // ── Flash ───────────────────────────────────────────────────────────
      const flashRoot = main.querySelector<HTMLDivElement>('#flash')!;
      if (flash) {
        const div = document.createElement('div');
        div.className = `alert ${flash.kind}`;
        div.textContent = flash.message;
        flashRoot.appendChild(div);
      }

      // ── Tier list ───────────────────────────────────────────────────────
      const tierList = main.querySelector<HTMLDivElement>('#tier-list')!;
      tiers.forEach((t, i) => {
        const row = document.createElement('div');
        row.className = 'tier-row' + (t.active ? ' active' : '') + (!t.owned && !t.upgradeable ? ' locked' : '');
        row.innerHTML = `
          <div class="tier-medal"><img class="tier-coin" src="/img/bank-coins/${t.slug}.webp" alt="" onerror="this.remove()" />${i + 1}</div>
          <div class="tier-body">
            <div class="name">${escapeHtml(t.name)}</div>
            <div class="blurb">${escapeHtml(t.blurb)}</div>
            ${cgTable([
              cgCol('Cost', [
                BigInt(t.upgradeCostCrowns) > 0n
                  ? cgRow('Upgrade', fmtBig(t.upgradeCostCrowns))
                  : cgRow('Tier', 'Starting'),
              ]),
              cgCol('Vault', [
                cgRow('Rate', `${t.ratePerDayPct}%/day`, 'gain'),
                cgRow('Cap', fmtBig(t.capCrowns)),
              ]),
            ])}
          </div>
          <div class="tier-cta">
            ${
              t.active
                ? '<span class="status owned">Current</span>'
                : t.owned
                  ? '<span class="status owned">Reached</span>'
                  : t.upgradeable
                    ? `<button class="btn" data-upgrade="${t.slug}">Upgrade</button>`
                    : '<span class="status">Locked</span>'
            }
          </div>
        `;
        tierList.appendChild(row);
      });

      // ── Wire deposit / withdraw / upgrade ──────────────────────────────
      const depInput = main.querySelector<HTMLInputElement>('#dep-amt')!;
      const wdInput = main.querySelector<HTMLInputElement>('#wd-amt')!;

      main.querySelectorAll<HTMLButtonElement>('button[data-fill]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const which = btn.dataset.fill!;
          if (which === 'dep') {
            // Max deposit: smaller of (wallet) or (cap - principal)
            const wallet = BigInt(char.crowns);
            const room = BigInt(bank!.capCrowns) - BigInt(bank!.principalCrowns);
            const max = wallet < room ? wallet : room;
            depInput.value = (max > 0n ? max : 0n).toString();
          } else {
            wdInput.value = bank!.principalCrowns;
          }
        });
      });

      main.querySelector<HTMLButtonElement>('#dep-btn')?.addEventListener('click', async () => {
        const amount = depInput.value.replace(/[^0-9]/g, '');
        if (!amount || amount === '0') {
          flash = { kind: 'warn', message: 'Enter a non-zero amount to deposit.' };
          anchored(renderMain);
          return;
        }
        try {
          const r = await api.depositBank({ amount });
          store.set({ character: r.character });
          bank = r.bank;
          tiers = r.tiers;
          flash = { kind: 'success', message: `Deposited ${fmtBig(amount)} crowns.` };
        } catch (err) {
          flash = { kind: 'danger', message: err instanceof HttpError ? err.message : 'Deposit failed.' };
        } finally {
          anchored(renderMain);
        }
      });

      main.querySelector<HTMLButtonElement>('#wd-btn')?.addEventListener('click', async () => {
        const amount = wdInput.value.replace(/[^0-9]/g, '');
        if (!amount || amount === '0') {
          flash = { kind: 'warn', message: 'Enter a non-zero amount to withdraw.' };
          anchored(renderMain);
          return;
        }
        try {
          const r = await api.withdrawBank({ amount });
          store.set({ character: r.character });
          bank = r.bank;
          tiers = r.tiers;
          flash = { kind: 'success', message: `Withdrew ${fmtBig(amount)} crowns.` };
        } catch (err) {
          flash = { kind: 'danger', message: err instanceof HttpError ? err.message : 'Withdrawal failed.' };
        } finally {
          anchored(renderMain);
        }
      });

      main.querySelectorAll<HTMLButtonElement>('button[data-upgrade]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const slug = btn.dataset.upgrade!;
          btn.disabled = true;
          try {
            const r = await api.upgradeBank({ toTierSlug: slug });
            store.set({ character: r.character });
            bank = r.bank;
            tiers = r.tiers;
            flash = { kind: 'success', message: `Promoted to ${r.bank.tierName}.` };
          } catch (err) {
            flash = { kind: 'danger', message: err instanceof HttpError ? err.message : 'Upgrade failed.' };
          } finally {
            anchored(renderMain);
          }
        });
      });

      // ── Next-payout countdown ─────────────────────────────────────────
      // Interest is paid as a single daily LUMP into the wallet (not a
      // continuous drip). Count down to the next payout time from the server.
      const nextEl = main.querySelector<HTMLDivElement>('#next-interest');
      const nextNote = main.querySelector<HTMLDivElement>('#next-interest-note');
      if (nextEl) {
        const perDay = bank.interestPerDayCrowns;
        const nextAt = Date.parse(bank.nextInterestAt);
        const principalNum = Number(bank.principalCrowns);
        const capacityFull = bank.capacityFull;
        const fmtClock = (totalSecs: number): string => {
          const s = Math.max(0, Math.ceil(totalSecs));
          const h = Math.floor(s / 3600);
          const m = Math.floor((s % 3600) / 60);
          const sec = s % 60;
          const pad = (n: number): string => String(n).padStart(2, '0');
          return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${m}:${pad(sec)}`;
        };
        const tick = (): void => {
          if (!nextEl.isConnected) { clearInterestTicker(); return; }
          if (perDay === '0' || principalNum <= 0) {
            nextEl.textContent = '—';
            if (nextNote) nextNote.textContent = 'deposit crowns to start earning interest';
            return;
          }
          const dest = capacityFull ? 'your wallet' : 'your vault';
          const secsLeft = (nextAt - Date.now()) / 1000;
          if (secsLeft <= 0) {
            nextEl.textContent = `+${fmtBig(perDay)}`;
            if (nextNote) nextNote.textContent = `due now — landing in ${dest}`;
            return;
          }
          nextEl.textContent = fmtClock(secsLeft);
          if (nextNote) nextNote.textContent = `+${fmtBig(perDay)} into ${dest}`;
        };
        tick();
        interestTicker = window.setInterval(tick, 1000);
      }
    };

    anchored(renderMain);
  });
}

// ── Helpers ───────────────────────────────────────────────────────────────

/** Short-form crown display — delegates to the canonical formatter. */
function fmtBig(s: string | number | bigint): string {
  return formatCrowns(s);
}

function capPct(b: BankDto): number {
  const cap = Number(BigInt(b.capCrowns));
  if (cap <= 0) return 0;
  const principal = Number(BigInt(b.principalCrowns));
  return Math.max(0, Math.min(100, (principal / cap) * 100));
}
